import { useCallback, useEffect, useRef, useState } from 'react';
import { isAllowlistedSupportAction } from '../constants/remoteSupportActions';
import { apiClient } from '../services/api';
import remoteControlSocket, {
  ChatMessagePayload,
  ControlCommandPayload,
  ControlEndedPayload,
  ControlGrantedPayload,
  FileReadyPayload,
  RemoteControlCommandType,
  SupportActionResultPayload,
} from '../services/remoteControlSocket';

export type RemoteControlStatus =
  | 'DISCONNECTED'
  | 'CONNECTING'
  | 'VIEW_ONLY'
  | 'CONTROL_REQUESTING'
  | 'CONTROLLING'
  | 'CONTROL_SUSPENDED'
  | 'RECONNECTING'
  | 'CONTROL_ENDED'
  | 'ERROR';

export type ChatMessageFrom = 'OPERATOR' | 'EMPLOYEE' | 'SYSTEM';

export interface RemoteChatMessage {
  id: string;
  from: ChatMessageFrom;
  text: string;
  at: string;
}

export interface ClipboardOpResult {
  ok: boolean;
  text?: string;
  error?: string;
  charCount?: number;
  reason?: string;
}

export type FileTransferDirection = 'UPLOAD' | 'DOWNLOAD';

export type FileTransferStatus =
  | 'pending'
  | 'uploading'
  | 'awaiting_remote'
  | 'ready'
  | 'completed'
  | 'cancelled'
  | 'error';

export interface FileTransferItem {
  transferId: string;
  direction: FileTransferDirection;
  fileName: string;
  status: FileTransferStatus;
  progress: number;
  error?: string;
  byteSize?: number;
}

export interface SupportActionResultItem {
  requestId: string;
  actionId: string;
  ok: boolean;
  error?: string;
  resultSummary?: string;
  recentLogLines?: string[];
  at: string;
}

const MOUSE_MOVE_COALESCE_MS = 33;
const PING_INTERVAL_MS = 20_000;

// Lock / secure desktop no longer suspend control — agent bridge continues input on Winlogon.
const SUSPEND_REASONS = new Set<string>();

function nextCommandId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cmd-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function nextChatId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `chat-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function nextLocalId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeChatMessage(payload: ChatMessagePayload): RemoteChatMessage {
  const atRaw = payload.at ?? payload.createdAt ?? Date.now();
  const at = typeof atRaw === 'number' ? new Date(atRaw).toISOString() : String(atRaw);
  return {
    id: String(payload.messageId || payload.id || nextChatId()),
    from: (payload.from === 'EMPLOYEE' || payload.from === 'OPERATOR' || payload.from === 'SYSTEM')
      ? payload.from
      : 'EMPLOYEE',
    text: String(payload.text || ''),
    at,
  };
}

function systemNote(text: string): RemoteChatMessage {
  return {
    id: nextChatId(),
    from: 'SYSTEM',
    text,
    at: new Date().toISOString(),
  };
}

export interface UseRemoteControlResult {
  status: RemoteControlStatus;
  isControlling: boolean;
  errorMessage: string | null;
  sessionId: string | null;
  /** In-hook memory only — never persisted to localStorage/sessionStorage. */
  controlToken: string | null;
  monitorId: number;
  startControl: (deviceId: string, monitorId?: number | string) => Promise<void>;
  stopControl: () => Promise<void>;
  sendMouseMove: (xRatio: number, yRatio: number, monitorId?: number) => void;
  sendMouseDown: (xRatio: number, yRatio: number, button?: number, monitorId?: number) => void;
  sendMouseUp: (xRatio: number, yRatio: number, button?: number, monitorId?: number) => void;
  sendMouseWheel: (xRatio: number, yRatio: number, delta: number, monitorId?: number) => void;
  sendKeyDown: (keyCode: number, virtualKey?: number) => void;
  sendKeyUp: (keyCode: number, virtualKey?: number) => void;
  sendClipboardToRemote: (text: string) => Promise<ClipboardOpResult>;
  getClipboardFromRemote: () => Promise<ClipboardOpResult>;
  sendChatMessage: (message: string) => Promise<void>;
  chatMessages: RemoteChatMessage[];
  unreadChatCount: number;
  clearUnread: () => void;
  openChat: () => void;
  closeChat: () => void;
  chatSessionEnded: boolean;
  requestRemoteFilePick: () => Promise<{ ok: boolean; error?: string; transferRequestId?: string }>;
  uploadFileToRemote: (file: File) => Promise<{ ok: boolean; transferId?: string; error?: string }>;
  cancelFileTransfer: (transferId: string) => Promise<{ ok: boolean; error?: string }>;
  executeSupportAction: (actionId: string) => Promise<{ ok: boolean; error?: string; requestId?: string }>;
  fileTransfers: FileTransferItem[];
  fileReady: FileTransferItem | null;
  actionResults: SupportActionResultItem[];
  clearFileReady: () => void;
}

export function useRemoteControl(): UseRemoteControlResult {
  const [status, setStatus] = useState<RemoteControlStatus>('VIEW_ONLY');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [controlToken, setControlToken] = useState<string | null>(null);
  const [monitorId, setMonitorId] = useState(0);
  const [chatMessages, setChatMessages] = useState<RemoteChatMessage[]>([]);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [chatSessionEnded, setChatSessionEnded] = useState(false);
  const [fileTransfers, setFileTransfers] = useState<FileTransferItem[]>([]);
  const [fileReady, setFileReady] = useState<FileTransferItem | null>(null);
  const [actionResults, setActionResults] = useState<SupportActionResultItem[]>([]);

  const sessionIdRef = useRef<string | null>(null);
  const controlTokenRef = useRef<string | null>(null);
  const monitorIdRef = useRef(0);
  const statusRef = useRef<RemoteControlStatus>('VIEW_ONLY');
  const deviceIdRef = useRef<string | null>(null);
  const chatOpenRef = useRef(false);
  const uploadAbortRef = useRef<Map<string, AbortController>>(new Map());

  const pendingMoveRef = useRef<{
    xRatio: number;
    yRatio: number;
    monitorId: number;
    timer: ReturnType<typeof setTimeout> | null;
  } | null>(null);

  const setStatusSafe = useCallback((next: RemoteControlStatus) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  const clearSessionLocal = useCallback(() => {
    sessionIdRef.current = null;
    controlTokenRef.current = null;
    setSessionId(null);
    setControlToken(null);
    if (pendingMoveRef.current?.timer) {
      clearTimeout(pendingMoveRef.current.timer);
    }
    pendingMoveRef.current = null;
    uploadAbortRef.current.forEach((ctrl) => ctrl.abort());
    uploadAbortRef.current.clear();
  }, []);

  const clearUnread = useCallback(() => {
    setUnreadChatCount(0);
  }, []);

  const openChat = useCallback(() => {
    chatOpenRef.current = true;
    setUnreadChatCount(0);
  }, []);

  const closeChat = useCallback(() => {
    chatOpenRef.current = false;
  }, []);

  const clearFileReady = useCallback(() => {
    setFileReady(null);
  }, []);

  const upsertTransfer = useCallback((item: FileTransferItem) => {
    setFileTransfers((prev) => {
      const idx = prev.findIndex((t) => t.transferId === item.transferId);
      if (idx < 0) return [...prev, item];
      const next = [...prev];
      next[idx] = { ...next[idx], ...item };
      return next;
    });
  }, []);

  const onControlEndedChat = useCallback(() => {
    setChatSessionEnded(true);
    setUnreadChatCount(0);
    setChatMessages((prev) => {
      if (prev.some((m) => m.from === 'SYSTEM' && m.text === 'Remote session ended')) {
        return prev;
      }
      return [...prev, systemNote('Remote session ended')];
    });
  }, []);

  const subscribeChatHistory = useCallback(() => {
    const sid = sessionIdRef.current;
    const token = controlTokenRef.current;
    if (!sid || !token) return;
    remoteControlSocket.subscribeChat({ sessionId: sid, controlToken: token });
  }, []);

  const flushMouseMove = useCallback(() => {
    const pending = pendingMoveRef.current;
    if (!pending) return;
    if (pending.timer) {
      clearTimeout(pending.timer);
      pending.timer = null;
    }
    const sid = sessionIdRef.current;
    const token = controlTokenRef.current;
    if (!sid || !token || statusRef.current !== 'CONTROLLING') {
      pendingMoveRef.current = null;
      return;
    }
    const payload: ControlCommandPayload = {
      sessionId: sid,
      controlToken: token,
      type: 'MOUSE_MOVE',
      commandId: nextCommandId(),
      xRatio: pending.xRatio,
      yRatio: pending.yRatio,
      monitorId: pending.monitorId,
    };
    pendingMoveRef.current = null;
    remoteControlSocket.sendCommand(payload);
  }, []);

  const emitImmediate = useCallback((
    type: Exclude<RemoteControlCommandType, 'MOUSE_MOVE'>,
    extras: Partial<ControlCommandPayload> = {},
  ) => {
    // Never drop clicks / keys behind a coalesced move — flush first.
    flushMouseMove();
    const sid = sessionIdRef.current;
    const token = controlTokenRef.current;
    if (!sid || !token || statusRef.current !== 'CONTROLLING') return;
    remoteControlSocket.sendCommand({
      sessionId: sid,
      controlToken: token,
      type,
      commandId: nextCommandId(),
      monitorId: extras.monitorId ?? monitorIdRef.current,
      ...extras,
    });
  }, [flushMouseMove]);

  const endLocally = useCallback((
    nextStatus: RemoteControlStatus,
    message?: string | null,
    options?: { noteSessionEnd?: boolean },
  ) => {
    const wasActive = Boolean(sessionIdRef.current)
      || statusRef.current === 'CONTROLLING'
      || statusRef.current === 'CONTROL_REQUESTING'
      || statusRef.current === 'CONTROL_SUSPENDED';
    clearSessionLocal();
    setErrorMessage(message ?? null);
    setStatusSafe(nextStatus);
    if (options?.noteSessionEnd !== false && wasActive) {
      onControlEndedChat();
    }
  }, [clearSessionLocal, onControlEndedChat, setStatusSafe]);

  useEffect(() => {
    const offGranted = remoteControlSocket.onGranted((payload: ControlGrantedPayload) => {
      sessionIdRef.current = payload.sessionId;
      controlTokenRef.current = payload.controlToken;
      const mid = Number(payload.monitorId ?? 0) || 0;
      monitorIdRef.current = mid;
      setSessionId(payload.sessionId);
      setControlToken(payload.controlToken);
      setMonitorId(mid);
      setErrorMessage(null);
      setChatSessionEnded(false);
      setStatusSafe('CONTROLLING');
      remoteControlSocket.subscribeChat({
        sessionId: payload.sessionId,
        controlToken: payload.controlToken,
      });
    });

    const offDenied = remoteControlSocket.onDenied((payload) => {
      clearSessionLocal();
      setErrorMessage(payload?.message || payload?.reason || 'Control denied');
      setStatusSafe('VIEW_ONLY');
    });

    const offEnded = remoteControlSocket.onEnded((payload: ControlEndedPayload) => {
      const reason = String(payload?.reason || '');
      if (SUSPEND_REASONS.has(reason)) {
        endLocally('CONTROL_SUSPENDED', payload?.reason || 'Control suspended', { noteSessionEnd: false });
        return;
      }
      if (reason === 'USER_STOPPED') {
        endLocally('VIEW_ONLY', null);
        return;
      }
      endLocally('CONTROL_ENDED', reason || 'Control ended');
    });

    const offError = remoteControlSocket.onError((payload) => {
      if (statusRef.current === 'CONTROLLING' || statusRef.current === 'CONTROL_REQUESTING') {
        endLocally('ERROR', payload?.message || 'Remote Control error');
      } else {
        setErrorMessage(payload?.message || 'Remote Control error');
      }
    });

    const offDisconnect = remoteControlSocket.onDisconnect(() => {
      if (
        statusRef.current === 'CONTROLLING'
        || statusRef.current === 'CONTROL_REQUESTING'
        || statusRef.current === 'CONTROL_SUSPENDED'
      ) {
        // Server releases the session; do not auto-resume.
        endLocally('VIEW_ONLY', 'Connection lost — control released');
      }
    });

    const offChatMessage = remoteControlSocket.onChatMessage((payload) => {
      const msg = normalizeChatMessage(payload);
      setChatMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      if (!chatOpenRef.current && msg.from === 'EMPLOYEE') {
        setUnreadChatCount((n) => n + 1);
      }
    });

    const offChatHistory = remoteControlSocket.onChatHistory((payload) => {
      const list = Array.isArray(payload?.messages) ? payload.messages.map(normalizeChatMessage) : [];
      setChatMessages(list);
      setChatSessionEnded(false);
    });

    const offChatSessionEnded = remoteControlSocket.onChatSessionEnded(() => {
      onControlEndedChat();
    });

    const offFileReady = remoteControlSocket.onFileReady((payload: FileReadyPayload) => {
      const transferId = String(payload?.transferId || '');
      if (!transferId) return;
      const item: FileTransferItem = {
        transferId,
        direction: 'DOWNLOAD',
        fileName: String(payload.fileName || 'remote-file'),
        status: 'ready',
        progress: 100,
        byteSize: payload.byteSize != null ? Number(payload.byteSize) : undefined,
      };
      upsertTransfer(item);
      setFileReady(item);
    });

    const offActionResult = remoteControlSocket.onSupportActionResult((payload: SupportActionResultPayload) => {
      const actionId = String(payload?.actionId || '');
      if (!actionId) return;
      const recentLogLines = Array.isArray(payload.recentLogLines)
        ? payload.recentLogLines.map((line) => String(line))
        : undefined;
      setActionResults((prev) => [
        ...prev,
        {
          requestId: String(payload.requestId || nextLocalId('action')),
          actionId,
          ok: Boolean(payload.ok),
          error: payload.error,
          resultSummary: payload.resultSummary,
          recentLogLines,
          at: new Date().toISOString(),
        },
      ]);
    });

    return () => {
      offGranted();
      offDenied();
      offEnded();
      offError();
      offDisconnect();
      offChatMessage();
      offChatHistory();
      offChatSessionEnded();
      offFileReady();
      offActionResult();
    };
  }, [clearSessionLocal, endLocally, onControlEndedChat, setStatusSafe, upsertTransfer]);

  // Heartbeat while controlling
  useEffect(() => {
    if (status !== 'CONTROLLING') return undefined;
    const timer = setInterval(() => {
      const sid = sessionIdRef.current;
      const token = controlTokenRef.current;
      if (!sid || !token) return;
      remoteControlSocket.ping({ sessionId: sid, controlToken: token });
    }, PING_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [status]);

  const startControl = useCallback(async (deviceId: string, nextMonitorId: number | string = 0) => {
    deviceIdRef.current = deviceId;
    setErrorMessage(null);
    setChatSessionEnded(false);
    setStatusSafe('CONNECTING');
    try {
      await remoteControlSocket.connect();
      setStatusSafe('CONTROL_REQUESTING');
      const granted = await remoteControlSocket.startControl({
        deviceId,
        monitorId: nextMonitorId,
      });
      // onGranted also fires; set state here in case ack arrives without event duplication issues
      sessionIdRef.current = granted.sessionId;
      controlTokenRef.current = granted.controlToken;
      const mid = Number(granted.monitorId ?? nextMonitorId ?? 0) || 0;
      monitorIdRef.current = mid;
      setSessionId(granted.sessionId);
      setControlToken(granted.controlToken);
      setMonitorId(mid);
      setStatusSafe('CONTROLLING');
      subscribeChatHistory();
    } catch (err: any) {
      clearSessionLocal();
      setErrorMessage(err?.message || 'Failed to start remote control');
      setStatusSafe('VIEW_ONLY');
    }
  }, [clearSessionLocal, setStatusSafe, subscribeChatHistory]);

  const stopControl = useCallback(async () => {
    const sid = sessionIdRef.current;
    const token = controlTokenRef.current;
    const hadSession = Boolean(sid && token);
    flushMouseMove();
    if (sid && token) {
      try {
        await remoteControlSocket.stopControl({ sessionId: sid, controlToken: token });
      } catch {
        // ignore — local release still applies
      }
    }
    clearSessionLocal();
    setErrorMessage(null);
    setStatusSafe('VIEW_ONLY');
    if (hadSession) {
      onControlEndedChat();
    }
  }, [clearSessionLocal, flushMouseMove, onControlEndedChat, setStatusSafe]);

  const sendMouseMove = useCallback((xRatio: number, yRatio: number, mid?: number) => {
    if (statusRef.current !== 'CONTROLLING') return;
    const monitor = mid ?? monitorIdRef.current;
    const existing = pendingMoveRef.current;
    if (existing) {
      existing.xRatio = xRatio;
      existing.yRatio = yRatio;
      existing.monitorId = monitor;
      if (!existing.timer) {
        existing.timer = setTimeout(() => flushMouseMove(), MOUSE_MOVE_COALESCE_MS);
      }
      return;
    }
    pendingMoveRef.current = {
      xRatio,
      yRatio,
      monitorId: monitor,
      timer: setTimeout(() => flushMouseMove(), MOUSE_MOVE_COALESCE_MS),
    };
  }, [flushMouseMove]);

  const sendMouseDown = useCallback((xRatio: number, yRatio: number, button = 0, mid?: number) => {
    emitImmediate('MOUSE_DOWN', { xRatio, yRatio, button, monitorId: mid ?? monitorIdRef.current });
  }, [emitImmediate]);

  const sendMouseUp = useCallback((xRatio: number, yRatio: number, button = 0, mid?: number) => {
    emitImmediate('MOUSE_UP', { xRatio, yRatio, button, monitorId: mid ?? monitorIdRef.current });
  }, [emitImmediate]);

  const sendMouseWheel = useCallback((xRatio: number, yRatio: number, delta: number, mid?: number) => {
    emitImmediate('MOUSE_WHEEL', { xRatio, yRatio, delta, monitorId: mid ?? monitorIdRef.current });
  }, [emitImmediate]);

  const sendKeyDown = useCallback((keyCode: number, virtualKey?: number) => {
    emitImmediate('KEY_DOWN', { keyCode, virtualKey: virtualKey ?? keyCode });
  }, [emitImmediate]);

  const sendKeyUp = useCallback((keyCode: number, virtualKey?: number) => {
    emitImmediate('KEY_UP', { keyCode, virtualKey: virtualKey ?? keyCode });
  }, [emitImmediate]);

  const sendClipboardToRemote = useCallback(async (text: string): Promise<ClipboardOpResult> => {
    if (statusRef.current !== 'CONTROLLING') {
      return { ok: false, error: 'Not controlling', reason: 'VIEW_ONLY' };
    }
    const sid = sessionIdRef.current;
    const token = controlTokenRef.current;
    if (!sid || !token) {
      return { ok: false, error: 'No active control session', reason: 'SESSION_INACTIVE' };
    }
    const ack = await remoteControlSocket.sendClipboardToRemote({
      sessionId: sid,
      controlToken: token,
      text,
    });
    return {
      ok: Boolean(ack?.ok),
      charCount: ack?.charCount,
      error: ack?.ok ? undefined : (ack?.error || ack?.message || ack?.reason || 'Clipboard send failed'),
      reason: ack?.reason,
    };
  }, []);

  const getClipboardFromRemote = useCallback(async (): Promise<ClipboardOpResult> => {
    if (statusRef.current !== 'CONTROLLING') {
      return { ok: false, error: 'Not controlling', reason: 'VIEW_ONLY' };
    }
    const sid = sessionIdRef.current;
    const token = controlTokenRef.current;
    if (!sid || !token) {
      return { ok: false, error: 'No active control session', reason: 'SESSION_INACTIVE' };
    }
    const ack = await remoteControlSocket.getClipboardFromRemote({
      sessionId: sid,
      controlToken: token,
    });
    return {
      ok: Boolean(ack?.ok),
      text: ack?.text,
      charCount: ack?.charCount,
      error: ack?.ok ? undefined : (ack?.error || ack?.message || ack?.reason || 'Clipboard get failed'),
      reason: ack?.reason,
    };
  }, []);

  const sendChatMessage = useCallback(async (message: string): Promise<void> => {
    if (statusRef.current !== 'CONTROLLING') {
      throw new Error('Not controlling');
    }
    const sid = sessionIdRef.current;
    const token = controlTokenRef.current;
    if (!sid || !token) {
      throw new Error('No active control session');
    }
    const trimmed = String(message || '').trim();
    if (!trimmed) return;
    await remoteControlSocket.sendChatMessage({
      sessionId: sid,
      controlToken: token,
      message: trimmed,
    });
  }, []);

  const requestRemoteFilePick = useCallback(async () => {
    if (statusRef.current !== 'CONTROLLING') {
      return { ok: false, error: 'Not controlling' };
    }
    const sid = sessionIdRef.current;
    const token = controlTokenRef.current;
    if (!sid || !token) {
      return { ok: false, error: 'No active control session' };
    }
    const localId = nextLocalId('pick');
    upsertTransfer({
      transferId: localId,
      direction: 'DOWNLOAD',
      fileName: 'Waiting for remote file…',
      status: 'awaiting_remote',
      progress: 0,
    });
    const ack = await remoteControlSocket.requestFileDownload({
      sessionId: sid,
      controlToken: token,
    });
    if (ack?.ok === false || ack?.success === false) {
      upsertTransfer({
        transferId: localId,
        direction: 'DOWNLOAD',
        fileName: 'Request failed',
        status: 'error',
        progress: 0,
        error: ack?.error || ack?.message || ack?.reason || 'File download request failed',
      });
      return {
        ok: false,
        error: ack?.error || ack?.message || ack?.reason || 'File download request failed',
      };
    }
    const transferRequestId = ack?.transferRequestId
      ? String(ack.transferRequestId)
      : localId;
    if (transferRequestId !== localId) {
      setFileTransfers((prev) => prev.filter((t) => t.transferId !== localId).concat([{
        transferId: transferRequestId,
        direction: 'DOWNLOAD',
        fileName: 'Waiting for remote file…',
        status: 'awaiting_remote',
        progress: 0,
      }]));
    }
    return { ok: true, transferRequestId };
  }, [upsertTransfer]);

  const uploadFileToRemote = useCallback(async (file: File) => {
    if (statusRef.current !== 'CONTROLLING') {
      return { ok: false, error: 'Not controlling' };
    }
    const sid = sessionIdRef.current;
    const token = controlTokenRef.current;
    if (!sid || !token) {
      return { ok: false, error: 'No active control session' };
    }
    const tempId = nextLocalId('upload');
    const abort = new AbortController();
    uploadAbortRef.current.set(tempId, abort);
    upsertTransfer({
      transferId: tempId,
      direction: 'UPLOAD',
      fileName: file.name,
      status: 'uploading',
      progress: 0,
      byteSize: file.size,
    });
    try {
      const result = await apiClient.monitoring.uploadRemoteControlFile(sid, token, file, {
        signal: abort.signal,
        onProgress: (percent) => {
          upsertTransfer({
            transferId: tempId,
            direction: 'UPLOAD',
            fileName: file.name,
            status: 'uploading',
            progress: percent,
            byteSize: file.size,
          });
        },
      });
      uploadAbortRef.current.delete(tempId);
      const transferId = result.transferId || tempId;
      if (transferId !== tempId) {
        setFileTransfers((prev) => prev.filter((t) => t.transferId !== tempId));
      }
      upsertTransfer({
        transferId,
        direction: 'UPLOAD',
        fileName: result.fileName || file.name,
        status: 'completed',
        progress: 100,
        byteSize: result.byteSize ?? file.size,
      });
      return { ok: true, transferId };
    } catch (err: any) {
      uploadAbortRef.current.delete(tempId);
      const cancelled = String(err?.message || '').toLowerCase().includes('cancel');
      upsertTransfer({
        transferId: tempId,
        direction: 'UPLOAD',
        fileName: file.name,
        status: cancelled ? 'cancelled' : 'error',
        progress: 0,
        byteSize: file.size,
        error: err?.message || 'Upload failed',
      });
      return { ok: false, error: err?.message || 'Upload failed' };
    }
  }, [upsertTransfer]);

  const cancelFileTransfer = useCallback(async (transferId: string) => {
    const localAbort = uploadAbortRef.current.get(transferId);
    if (localAbort) {
      localAbort.abort();
      uploadAbortRef.current.delete(transferId);
    }
    try {
      await apiClient.monitoring.cancelRemoteControlFileTransfer(transferId);
      upsertTransfer({
        transferId,
        direction: 'UPLOAD',
        fileName: '',
        status: 'cancelled',
        progress: 0,
      });
      setFileTransfers((prev) => prev.map((t) => (
        t.transferId === transferId ? { ...t, status: 'cancelled' as const } : t
      )));
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err?.response?.data?.message || err?.message || 'Cancel failed' };
    }
  }, [upsertTransfer]);

  const executeSupportAction = useCallback(async (actionId: string) => {
    if (!isAllowlistedSupportAction(actionId)) {
      return { ok: false, error: 'Unknown action' };
    }
    if (statusRef.current !== 'CONTROLLING') {
      return { ok: false, error: 'Not controlling' };
    }
    const sid = sessionIdRef.current;
    const token = controlTokenRef.current;
    if (!sid || !token) {
      return { ok: false, error: 'No active control session' };
    }
    const ack = await remoteControlSocket.executeSupportAction({
      sessionId: sid,
      controlToken: token,
      actionId,
    });
    if (ack?.ok === false || ack?.success === false) {
      const error = ack?.error || ack?.message || ack?.reason || 'Action failed';
      setActionResults((prev) => [
        ...prev,
        {
          requestId: String(ack?.requestId || nextLocalId('action')),
          actionId,
          ok: false,
          error,
          at: new Date().toISOString(),
        },
      ]);
      return { ok: false, error, requestId: ack?.requestId };
    }
    return { ok: true, requestId: ack?.requestId };
  }, []);

  // Cleanup pending coalesce timer on unmount
  useEffect(() => () => {
    if (pendingMoveRef.current?.timer) {
      clearTimeout(pendingMoveRef.current.timer);
    }
    uploadAbortRef.current.forEach((ctrl) => ctrl.abort());
    uploadAbortRef.current.clear();
  }, []);

  return {
    status,
    isControlling: status === 'CONTROLLING',
    errorMessage,
    sessionId,
    controlToken,
    monitorId,
    startControl,
    stopControl,
    sendMouseMove,
    sendMouseDown,
    sendMouseUp,
    sendMouseWheel,
    sendKeyDown,
    sendKeyUp,
    sendClipboardToRemote,
    getClipboardFromRemote,
    sendChatMessage,
    chatMessages,
    unreadChatCount,
    clearUnread,
    openChat,
    closeChat,
    chatSessionEnded,
    requestRemoteFilePick,
    uploadFileToRemote,
    cancelFileTransfer,
    executeSupportAction,
    fileTransfers,
    fileReady,
    actionResults,
    clearFileReady,
  };
}

export default useRemoteControl;
